import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/providers/auth-provider';
import type { Business, BusinessSubscription, SubscriptionPlan } from '@shared/schema';

export type BusinessWithSubscription = Business & {
  subscription: (BusinessSubscription & {
    planName?: string;
    monthlyPrice?: string | null;
    plan?: Pick<SubscriptionPlan, 'name'>;
  }) | null;
};

interface BusinessContextType {
  businesses: BusinessWithSubscription[];
  isLoading: boolean;
  error: Error | null;
  activeBusiness: BusinessWithSubscription | null;
  activeBusinessId: number | null;
  setActiveBusinessId: (id: number) => void;
  refreshBusinesses: () => void;
  needsBusinessSelection: boolean;
}

const BusinessContext = createContext<BusinessContextType>({
  businesses: [],
  isLoading: false,
  error: null,
  activeBusiness: null,
  activeBusinessId: null,
  setActiveBusinessId: () => {},
  refreshBusinesses: () => {},
  needsBusinessSelection: false,
});

export const useBusinessContext = () => {
  const context = useContext(BusinessContext);
  if (!context) {
    throw new Error("useBusinessContext must be used within a BusinessProvider");
  }
  return context;
};

export const useBusiness = () => useBusinessContext();

function storageKey(userId: number | undefined): string {
  return userId ? `activeBusinessId_user_${userId}` : 'activeBusinessId';
}

/**
 * Platform admin detection — checks email and username directly.
 * Does NOT rely on isAdmin flag from API (which may not be present in login response).
 */
function isPlatformAdmin(user: any): boolean {
  if (!user) return false;
  return user.email === 'hello@zentrobooks.com';
}

export const BusinessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const prevUserIdRef = useRef<number | null>(null);
  const queryClient = useQueryClient();

  const [activeBusinessId, setActiveBusinessIdState] = useState<number | null>(null);

  // When user changes (login/logout/switch), reset business context
  useEffect(() => {
    const newUserId = user?.id ?? null;
    const prevUserId = prevUserIdRef.current;

    if (newUserId !== prevUserId) {
      const admin = isPlatformAdmin(user);
      console.log(`[CTX] User changed: ${prevUserId} → ${newUserId} isAdmin=${admin} email=${user?.email}`);
      prevUserIdRef.current = newUserId;

      if (newUserId) {
        if (admin) {
          // Platform admin: ALWAYS start with no business. Must pick.
          console.log(`[CTX] Admin login — clearing business selection, must choose`);
          setActiveBusinessIdState(null);
          localStorage.removeItem(storageKey(newUserId));
          localStorage.removeItem('activeBusinessId');
        } else {
          // Regular user: try restoring saved selection
          const key = storageKey(newUserId);
          const saved = localStorage.getItem(key);
          if (saved) {
            const savedId = parseInt(saved);
            console.log(`[CTX] Restoring businessId=${savedId} for userId=${newUserId}`);
            setActiveBusinessIdState(savedId);

            // Sync with backend session — staff uses its own assignment-checked endpoint
            const isStaff = !!(user as any)?.isStaff;
            const selectUrl = isStaff ? '/api/staff/my/select-business' : '/api/businesses/select';
            fetch(selectUrl, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ businessId: savedId }),
            }).then(r => {
              if (r.ok) console.log(`[CTX] Backend session restored to businessId=${savedId}`);
              else console.error(`[CTX] Backend session restore failed`);
            }).catch(err => console.error(`[CTX] Backend session restore error:`, err));
          } else {
            setActiveBusinessIdState(null);
          }
        }
      } else {
        // Logged out
        setActiveBusinessIdState(null);
        localStorage.removeItem('activeBusinessId');
      }

      queryClient.invalidateQueries({ queryKey: ['/api/businesses'] });
    }
  }, [user?.id, user?.email, queryClient]);

  // Fetch businesses when user is authenticated (skip for platform admin)
  const isAdmin = isPlatformAdmin(user);
  const { data: businesses = [], isLoading, error } = useQuery<BusinessWithSubscription[]>({
    queryKey: ['/api/businesses'],
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    retry: 1,
    refetchOnWindowFocus: true,
    enabled: !!user && !isAdmin,
  });

  // Auto-select logic — runs when businesses load
  useEffect(() => {
    if (isLoading || !user) return;

    const admin = isPlatformAdmin(user);

    // Platform admin: NEVER auto-select. They must choose.
    if (admin) {
      if (activeBusinessId && !businesses.find(b => b.id === activeBusinessId)) {
        console.log(`[CTX] Admin: saved businessId=${activeBusinessId} not in list, clearing`);
        setActiveBusinessIdState(null);
        localStorage.removeItem(storageKey(user.id));
        localStorage.removeItem('activeBusinessId');
      }
      // Do NOT auto-select anything
      return;
    }

    // Regular user: validate current selection, but do NOT auto-select.
    // If no saved selection, the welcome dashboard renders and the user picks explicitly.
    if (businesses.length > 0) {
      if (activeBusinessId) {
        const currentValid = businesses.find(b => b.id === activeBusinessId);
        if (!currentValid) {
          console.log(`[CTX] Saved businessId=${activeBusinessId} not found in list, clearing`);
          setActiveBusinessIdState(null);
          localStorage.removeItem(storageKey(user.id));
          localStorage.removeItem('activeBusinessId');
        }
      }
      // No auto-select — welcome dashboard handles the "pick a business" flow
    } else {
      setActiveBusinessIdState(null);
      localStorage.removeItem(storageKey(user.id));
      localStorage.removeItem('activeBusinessId');
    }
  }, [businesses, isLoading, user]);

  const activeBusiness = activeBusinessId
    ? businesses.find((b) => b.id === activeBusinessId) || null
    : null;

  const setActiveBusinessId = useCallback(async (id: number) => {
    const business = businesses.find(b => b.id === id);
    if (business) {
      console.log(`[CTX] Business selected: ${business.name} (ID: ${id}) userId=${user?.id}`);

      // Set backend session FIRST — all API calls depend on this
      try {
        const isStaff = !!(user as any)?.isStaff;
        const selectUrl = isStaff ? '/api/staff/my/select-business' : '/api/businesses/select';
        const res = await fetch(selectUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ businessId: id }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          console.error(`[CTX] Backend rejected business select:`, err);
          return; // Don't set locally if backend rejects
        }
        console.log(`[CTX] Backend session set to businessId=${id}`);
      } catch (err) {
        console.error(`[CTX] Failed to set backend session:`, err);
        return;
      }

      setActiveBusinessIdState(id);
      if (user) localStorage.setItem(storageKey(user.id), id.toString());
      localStorage.setItem('activeBusinessId', id.toString());

      // Invalidate all queries so they refetch with new session context
      queryClient.invalidateQueries();
    } else {
      console.warn(`[CTX] Business ID ${id} not in user's list — rejected`);
    }
  }, [businesses, user, queryClient]);

  const refreshBusinesses = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['/api/businesses'] });
  }, [queryClient]);

  const needsBusinessSelection = !isLoading && businesses.length > 0 && !activeBusiness;

  return (
    <BusinessContext.Provider
      value={{
        businesses,
        activeBusinessId,
        setActiveBusinessId,
        activeBusiness,
        isLoading,
        error: error as Error | null,
        refreshBusinesses,
        needsBusinessSelection,
      }}
    >
      {children}
    </BusinessContext.Provider>
  );
};
